import { create } from 'zustand';
import ServiceCBO from '@/services/ServiceCBO';
import { notify } from '@/stores/useToast';

export interface CboMovimentacao {
  [key: string]: unknown;
  eventoId: number | string;
  dataOcorrencia?: string | null;
  registerDate?: string | null;
  idDocument?: string | null;
  submissionType?: string | null;
  receiptNumber?: string | null;
  receiptNumberRectification?: string | null;
  version?: string | null;
  esocialEventCode?: string | null;
  esocialEventDescription?: string | null;
  excluded?: boolean;
  importDate?: string | null;
}

export interface CboEventosFiltros {
  rootCnpjList: string[];
  importDate: string[];
  registerDate: string[];
  dataOcorrencia: string[];
  submissionType: string[];
  version: string[];
  esocialEventCode: string[];
  search: string;
}

export interface CboEventosOptions {
  versions: string[];
  events: { code: string; description: string | null }[];
}

export const FILTROS_CBO_EVENTOS_VAZIOS: CboEventosFiltros = {
  rootCnpjList: [],
  importDate: [],
  registerDate: [],
  dataOcorrencia: [],
  submissionType: [],
  version: [],
  esocialEventCode: [],
  search: '',
};

// Sort aliases defined by the CBO events API for the visible event columns.
export const CBO_EVENTOS_SORT_ALIASES: Record<string, string> = {
  dataOcorrencia: 'dataOcorrencia',
  registerDate: 'registerDate',
  idDocument: 'idDocument',
  submissionType: 'submissionType',
  receiptNumber: 'receiptNumber',
  receiptNumberRectification: 'receiptNumberRectification',
  version: 'version',
  esocialEventDescription: 'esocialEventCode',
  importDate: 'importDate',
};

interface State {
  eventos: CboMovimentacao[];
  total: number;
  page: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filtros: CboEventosFiltros;
  opcoes: CboEventosOptions;
  carregando: boolean;
  exportando: boolean;
  inicializar: (trabalhadorId: number) => Promise<void>;
  carregarEventos: (trabalhadorId: number) => Promise<void>;
  carregarFiltros: (trabalhadorId: number) => Promise<void>;
  aplicarFiltros: (trabalhadorId: number, filtros: CboEventosFiltros) => Promise<void>;
  setPage: (trabalhadorId: number, page: number) => Promise<void>;
  setSort: (trabalhadorId: number, sortBy: string, sortOrder: 'asc' | 'desc') => Promise<void>;
  exportar: (trabalhadorId: number) => Promise<void>;
}

// Descarta respostas obsoletas (outro trabalhador, página ou ordenação anterior) que
// cheguem depois da requisição mais recente.
let eventosRequestId = 0;
let filtrosRequestId = 0;

export const useCboMovimentacoesStore = create<State>((set, get) => ({
  eventos: [],
  total: 0,
  page: 0,
  sortBy: 'dataOcorrencia',
  sortOrder: 'desc',
  filtros: { ...FILTROS_CBO_EVENTOS_VAZIOS },
  opcoes: { versions: [], events: [] },
  carregando: false,
  exportando: false,

  async inicializar(trabalhadorId) {
    set({
      eventos: [],
      total: 0,
      page: 0,
      sortBy: 'dataOcorrencia',
      sortOrder: 'desc',
      filtros: { ...FILTROS_CBO_EVENTOS_VAZIOS },
      opcoes: { versions: [], events: [] },
    });
    await Promise.all([get().carregarFiltros(trabalhadorId), get().carregarEventos(trabalhadorId)]);
  },

  async carregarEventos(trabalhadorId) {
    const request = ++eventosRequestId;
    set({ carregando: true });
    try {
      const state = get();
      const sortAlias = CBO_EVENTOS_SORT_ALIASES[state.sortBy] ?? 'dataOcorrencia';
      const { data } = await ServiceCBO.listarEventos(trabalhadorId, state.filtros, {
        page: state.page,
        size: 50,
        sort: `${sortAlias},${state.sortOrder}`,
      });
      if (request !== eventosRequestId) return;
      set({ eventos: data.content ?? [], total: data.totalElements ?? 0 });
    } catch {
      if (request !== eventosRequestId) return;
      set({ eventos: [], total: 0 });
      notify.error('Erro ao carregar as movimentações.');
    } finally {
      if (request === eventosRequestId) set({ carregando: false });
    }
  },

  async carregarFiltros(trabalhadorId) {
    const request = ++filtrosRequestId;
    try {
      const { data } = await ServiceCBO.filtrosEventos(trabalhadorId);
      if (request !== filtrosRequestId) return;
      set({ opcoes: { versions: data.versions ?? [], events: data.events ?? [] } });
    } catch {
      if (request !== filtrosRequestId) return;
      set({ opcoes: { versions: [], events: [] } });
      notify.error('Erro ao carregar os filtros das movimentações.');
    }
  },

  async aplicarFiltros(trabalhadorId, filtros) {
    set({ filtros, page: 0 });
    await get().carregarEventos(trabalhadorId);
  },

  async setPage(trabalhadorId, page) {
    set({ page });
    await get().carregarEventos(trabalhadorId);
  },

  async setSort(trabalhadorId, sortBy, sortOrder) {
    if (!CBO_EVENTOS_SORT_ALIASES[sortBy]) return;
    set({ sortBy, sortOrder, page: 0 });
    await get().carregarEventos(trabalhadorId);
  },

  async exportar(trabalhadorId) {
    set({ exportando: true });
    try {
      const state = get();
      const sortAlias = CBO_EVENTOS_SORT_ALIASES[state.sortBy] ?? 'dataOcorrencia';
      await ServiceCBO.exportarEventos(trabalhadorId, state.filtros, {
        sort: `${sortAlias},${state.sortOrder}`,
      });
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Erro ao exportar as movimentações.');
    } finally {
      set({ exportando: false });
    }
  },
}));
